import React from 'react';
import PropTypes from 'prop-types';
import { forgeClassHelper } from '../utils/classes';
import CheckboxInput from './CheckboxInput';

const classes = forgeClassHelper('checkbox');

const Checkbox = React.forwardRef((props, ref) => {
  const {
    className,
    children,
    error,
    description,
    id,
    indeterminate,
    required,
    disabled,
    hideRequiredStyles,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    value,
    valueAttribute,
    ...inputProps
  } = props;

  const descriptionLabelId = `description-label-${id}`;

  // combine any provided aria-labelledby with ours
  // (ours: input labelled by description)
  const combinedAriaLabelledby = ariaLabelledby ? `${ariaLabelledby} ${descriptionLabelId}` : descriptionLabelId;

  // set value from valueAttribute prop if provided,
  // if not then use value prop
  const checkboxValue = valueAttribute ? { value: valueAttribute } : value && value !== '' ? { value: value } : null;

  const useErrorStyles = error && !disabled;
  const useRequiredStyles = required && !hideRequiredStyles && !disabled;

  return (
    <span
      {...classes({
        states: {
          disabled,
          error: useErrorStyles,
          required: useRequiredStyles,
        },
        extra: className,
      })}
    >
      <CheckboxInput
        {...inputProps}
        aria-labelledby={combinedAriaLabelledby}
        id={id}
        disabled={disabled}
        defaultChecked={inputProps.defaultChecked}
        error={error}
        indeterminate={indeterminate}
        ref={ref}
        required={required}
        {...checkboxValue}
      />
      <label
        {...classes({
          element: 'description',
          states: {
            disabled,
            error: useErrorStyles,
            required: useRequiredStyles,
            indeterminate,
          },
          extra: 'fe_c_label',
        })}
        id={descriptionLabelId}
        htmlFor={id}
        aria-label={ariaLabel}
      >
        {description}
        {children}
      </label>
    </span>
  );
});

Checkbox.displayName = 'Checkbox';

// This is only used by the form-redux-form package and can probably be removed in Forge 4
Checkbox.validateRequired = (value, options) => {
  if (value !== true) {
    return (options && options.message) || 'This field is required.';
  }
};

Checkbox.propsFromValue = (value) => ({ checked: !!value });

Checkbox.disableLabelFor = true;

Checkbox.propTypes = {
  /** aria-label to apply to the `<label>` tag */
  'aria-label': PropTypes.string,
  /** Specify an additional element ID to label the input. (The input is automatically labeled by the input label, so specifying this additional label target merely provides an additional label.) */
  'aria-labelledby': PropTypes.string,
  /** Whether the Checkbox should be checked. Used for [controlled](https://reactjs.org/docs/forms.html#controlled-components) components. */
  checked: PropTypes.bool,
  /** Content to render after the description prop */
  children: PropTypes.node,
  /** Adds a class to the root element of the component */
  className: PropTypes.string,
  /** Whether the Checkbox should be checked. Used for [uncontrolled](https://reactjs.org/docs/uncontrolled-components.html) Checkboxes. */
  defaultChecked: PropTypes.bool,
  /** A descriptive label for what the Checkbox indicates; behaves like a standard input label */
  description: PropTypes.node.isRequired,
  /** Adds the relevant Forge state class to the input and passes the prop down to the native react `<input />` */
  disabled: PropTypes.bool,
  /** Highlights the input when defined (specific string doesn't matter) */
  error: PropTypes.string,
  /** Hides any required styling. Can be set manually or by the required field variation set for `Form`. */
  hideRequiredStyles: PropTypes.bool,
  /** Id is sent to the `<input />`, and is used to associate the label with the checkbox. */
  id: PropTypes.string.isRequired,
  /** If the checkbox is neither true nor false */
  indeterminate: PropTypes.bool,
  /** Indicates if it is a required field. Passed as an attribute to the native react `<input />` */
  required: PropTypes.bool,
  /** *This prop should not be used, except internally by certain components.*
   *
   * If `value` is provided and `valueAttribute` is not provided, this prop is used to set the value attribute on `<input>`
   *
   * To set the checkbox status, use `checked` or `defaultChecked`.\
   * To set the value attribute on the `<input>`, use `valueAttribute`.
   */
  value: PropTypes.string,
  /** Sets the value attribute on the native `<input type="checkbox">`.*/
  valueAttribute: PropTypes.string,
  /** Passthrough props to the `<input>` element */
  '...rest': PropTypes.any,
};

Checkbox.defaultProps = {
  disabled: false,
  hideRequiredStyles: false,
  required: false,
};

export default Checkbox;
